import React, { useState } from "react";
import { Phone, ShieldCheck, Send, Loader2, LifeBuoy, AlertTriangle, ChevronDown, ChevronUp, FileDown } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const BRANCHES = ["Army", "Navy", "Marine Corps", "Air Force", "Coast Guard", "Space Force", "National Guard", "Reserves", "Prefer not to say"];
const HOUSING = [
    { value: "street", label: "On the street / in a vehicle" },
    { value: "shelter", label: "Emergency shelter" },
    { value: "transitional", label: "Transitional / temporary housing" },
    { value: "couch", label: "Couch-surfing / staying with someone" },
    { value: "at_risk", label: "At risk of losing housing this month" },
    { value: "other", label: "Other situation" },
];
const NEEDS = [
    { id: "housing", label: "Housing" },
    { id: "food", label: "Food" },
    { id: "wardrobe", label: "Clothing / professional wardrobe" },
    { id: "training", label: "Job training / certification" },
    { id: "childcare", label: "Childcare" },
    { id: "mental_health", label: "Mental health / counseling" },
    { id: "other", label: "Other / not sure" },
];

const EMPTY = {
    name: "",
    relationship: "veteran",
    phone: "",
    email: "",
    branch: "",
    housing_status: "",
    children_count: 0,
    urgent_needs: [],
    location: "",
    notes: "",
};

export default function VeteranIntakeForm() {
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const toggleNeed = (id) => {
        setForm((f) => ({
            ...f,
            urgent_needs: f.urgent_needs.includes(id)
                ? f.urgent_needs.filter((n) => n !== id)
                : [...f.urgent_needs, id],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.housing_status) {
            toast.error("Please fill in your name, phone, and current housing situation.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/intake", { ...form, children_count: Number(form.children_count) || 0 });
            setSubmitted(true);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please call us at (972) 942-9022.");
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="faith-card p-10 text-center" data-testid="intake-success">
                <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-5">
                    <ShieldCheck className="w-8 h-8" />
                </div>
                <h3 className="font-serif text-3xl text-[var(--brand-navy)] mb-3">We received your request.</h3>
                <p className="text-slate-600 leading-relaxed mb-6 max-w-md mx-auto">
                    Someone from our team will reach out to the phone number you provided within <strong>24 hours</strong>. You are seen. You are not alone.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <a href="tel:+19729429022" className="btn-gold" data-testid="intake-success-call">
                        <Phone className="w-4 h-4 mr-2" /> Or call us now: (972) 942-9022
                    </a>
                    <a href="/veteran-resources.pdf" target="_blank" rel="noopener noreferrer" className="btn-outline-navy" data-testid="intake-success-pdf">
                        <FileDown className="w-4 h-4 mr-2" /> Immediate Resources (PDF)
                    </a>
                </div>
                <div className="mt-4 text-xs text-slate-500 max-w-md mx-auto">
                    The PDF has emergency hotlines, VA housing programs, and local Texas shelters — read it while you wait for our callback.
                </div>
                <div className="mt-6 text-xs text-slate-500 italic">
                    You are seen. You are not alone. We will be in touch.
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Crisis banner */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-start gap-3" data-testid="crisis-banner">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-red-900 leading-relaxed">
                    <strong className="block text-base mb-1">In crisis or thinking of self-harm?</strong>
                    Call the <strong>Veterans Crisis Line: dial 988, then press 1</strong>, or text <strong>838255</strong>. Free. Confidential. 24/7.
                    <a href="/veteran-resources.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-white text-red-700 border border-red-300 rounded-full text-xs font-semibold hover:bg-red-100" data-testid="crisis-pdf-btn">
                        <FileDown className="w-3.5 h-3.5" /> Download the Veteran Resource One-Pager
                    </a>
                </div>
            </div>

            <form onSubmit={submit} className="faith-card p-7 sm:p-9 space-y-5" data-testid="intake-form">
                <div className="flex items-start gap-3 pb-2">
                    <LifeBuoy className="w-6 h-6 text-[var(--brand-coral)] shrink-0 mt-1" />
                    <div>
                        <h3 className="font-serif text-2xl text-[var(--brand-navy)]">Request Intake</h3>
                        <p className="text-sm text-slate-600 mt-1">
                            Confidential. No judgment. We'll call you back within 24 hours.
                        </p>
                    </div>
                </div>

                {/* Who is filling this out */}
                <div>
                    <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Who is filling this out? *</span>
                    <div className="grid grid-cols-2 gap-3">
                        {[
                            { value: "veteran", label: "I am the veteran" },
                            { value: "advocate", label: "I'm helping a veteran" },
                        ].map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setForm({ ...form, relationship: opt.value })}
                                className={`rounded-xl px-4 py-3 text-sm font-medium border-2 transition-all ${
                                    form.relationship === opt.value
                                        ? "border-[var(--brand-gold)] bg-[var(--brand-gold)]/10 text-[var(--brand-navy)]"
                                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                                }`}
                                data-testid={`intake-relationship-${opt.value}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label={form.relationship === "advocate" ? "Your name *" : "Veteran's name *"}>
                        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ifield" data-testid="intake-name" />
                    </Field>
                    <Field label="Best phone to reach you *">
                        <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ifield" placeholder="(555) 555-5555" data-testid="intake-phone" />
                    </Field>
                    <Field label="Email (if available)">
                        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ifield" data-testid="intake-email" />
                    </Field>
                    <Field label="Your city / state (if known)">
                        <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="ifield" placeholder="e.g. Lewisville, TX" data-testid="intake-location" />
                    </Field>
                </div>

                {/* Housing status */}
                <Field label="Current housing situation *">
                    <select required value={form.housing_status} onChange={(e) => setForm({ ...form, housing_status: e.target.value })} className="ifield" data-testid="intake-housing">
                        <option value="">Select one...</option>
                        {HOUSING.map((h) => (
                            <option key={h.value} value={h.value}>{h.label}</option>
                        ))}
                    </select>
                </Field>

                {/* Urgent needs */}
                <div>
                    <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">What do you need help with most? (select all that apply)</span>
                    <div className="grid sm:grid-cols-2 gap-2" data-testid="intake-needs">
                        {NEEDS.map((n) => {
                            const active = form.urgent_needs.includes(n.id);
                            return (
                                <button
                                    key={n.id}
                                    type="button"
                                    onClick={() => toggleNeed(n.id)}
                                    className={`text-left rounded-xl px-4 py-2.5 text-sm border-2 transition-all flex items-center ${
                                        active
                                            ? "border-[var(--brand-gold)] bg-[var(--brand-gold)]/10 text-[var(--brand-navy)]"
                                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                                    }`}
                                    data-testid={`intake-need-${n.id}`}
                                >
                                    <span className={`w-4 h-4 rounded mr-2.5 border-2 ${active ? "bg-[var(--brand-gold)] border-[var(--brand-gold)]" : "border-slate-300"}`} />
                                    {n.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Toggle for optional fields */}
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="text-sm font-semibold text-[var(--brand-navy)] hover:text-[var(--brand-coral)] inline-flex items-center"
                    data-testid="intake-more-fields-toggle"
                >
                    {expanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                    {expanded ? "Hide" : "Add"} optional details (helps us connect you to the right program faster)
                </button>

                {expanded && (
                    <div className="space-y-4 border-t border-slate-100 pt-5" data-testid="intake-optional">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Branch of service">
                                <select value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} className="ifield" data-testid="intake-branch">
                                    <option value="">Select...</option>
                                    {BRANCHES.map((b) => (<option key={b}>{b}</option>))}
                                </select>
                            </Field>
                            <Field label="Number of children with you">
                                <input type="number" min="0" max="20" value={form.children_count} onChange={(e) => setForm({ ...form, children_count: e.target.value })} className="ifield" data-testid="intake-children" />
                            </Field>
                        </div>
                        <Field label="Anything else you'd like us to know">
                            <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="ifield resize-none" placeholder="Share what you're comfortable sharing — your story, your goals, what's most urgent." data-testid="intake-notes" />
                        </Field>
                    </div>
                )}

                <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-50" data-testid="intake-submit-btn">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4 mr-2" /> Request Help</>}
                </button>

                <div className="text-xs text-slate-500 text-center leading-relaxed pt-2">
                    Or call us directly: <a href="tel:+19729429022" className="font-semibold text-[var(--brand-navy)]">(972) 942-9022</a><br />
                    Your information is private and used only to connect you to help.
                </div>

                <style>{`.ifield { width:100%; padding: 0.75rem 1rem; border-radius: 0.75rem; border: 1px solid #e2e8f0; outline:none; background:white; font-size: 0.95rem; } .ifield:focus { border-color: var(--brand-gold); box-shadow: 0 0 0 3px rgba(245,185,71,0.15); }`}</style>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</span>
            {children}
        </label>
    );
}
